import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { attachWebLifecycle } from '@rhwb/usage';
import { useAuth } from '../contexts/AuthContext';
import { usageTracker } from '../lib/usageTracker';

/**
 * Route keys, kept stable and free of parameters. A key must never carry an
 * email or id — see the COMMENT ON app_usage_page_stats.page_key.
 */
function pageKeyFor(pathname: string): string | null {
  if (pathname.startsWith('/auth')) return null; // pre-auth, nothing to attribute
  if (pathname === '/' || pathname === '') return 'dashboard';
  if (pathname.startsWith('/veer')) return 'veer';
  if (pathname.startsWith('/profile')) return 'profile';
  if (pathname.startsWith('/survey') || pathname.startsWith('/season15-survey')) return 'nps-survey';
  return pathname.replace(/^\//, '').split('/')[0] || 'dashboard';
}

/**
 * Renders nothing; exists to bind the tracker to auth state and the router.
 * Must sit inside both BrowserRouter (for useLocation) and AuthProvider.
 */
export default function UsageTracker() {
  const { user, isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const email = user?.email ?? null;
  const pageKey = pageKeyFor(pathname);

  useEffect(() => {
    if (!isAuthenticated || !email) return undefined;
    usageTracker.start(pageKey ?? 'dashboard', { email });
    return () => usageTracker.stop();
    // The initial page is captured on start; the effect below reports moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, email]);

  useEffect(() => {
    if (!isAuthenticated || !pageKey) return;
    usageTracker.setPage(pageKey);
  }, [isAuthenticated, pageKey]);

  // Browsers throttle timers in hidden tabs, so a minimised window would
  // otherwise keep accruing time in long, unreliable gaps.
  useEffect(() => attachWebLifecycle(usageTracker), []);

  return null;
}
